import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { HOME, type Solution } from '@/data/home';
import type { ArticleItem } from '@components/pages/ResourceSections';
import FigmaFrame from './FigmaFrame';
import { Img } from '@/components/Img';

const STATUE = '/landing/tackled-impact/statue.webp';
/** The band's slides, read at render so the hydrated list is what shows. */
const solutions = (): Solution[] => HOME.tackled.solutions;

/**
 * The "Top Success Stories" strip under a slide: what the CMS attached to it.
 * Until the CMS has answered there is nothing real to show, so the strip is
 * empty rather than repeating a sample line.
 */
const storiesOf = (sol: Solution): ArticleItem[] => sol.stories ?? [];

/* ── Icons ───────────────────────────────────────────────────────────── */
function ArrowUpRight({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden>
            <path d="M9 23L23 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 9H23V22" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function ArrowLeft({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden>
            <path
                d="M20 6L10 16l10 10"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
function CloseIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden>
            <path d="M9 9l14 14M23 9L9 23" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
    );
}

/** Reusable in-view trigger (fires once). */
function useInView<T extends HTMLElement>() {
    const ref = useRef<T>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setInView(true);
                    io.disconnect();
                }
            },
            { threshold: 0.2 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);
    return { ref, inView };
}

/* ── Desktop ─────────────────────────────────────────────────────────── */
function TwoTone({ top, bottom, className }: { top: string; bottom: string; className?: string }) {
    // Each tone sits on its own line so every solution's heading is exactly two
    // lines (colour lead-in, then dark remainder) regardless of length.
    return (
        <p className={className}>
            <span className="block text-[#6a6854]">{top}</span>
            <span className="block">{bottom}</span>
        </p>
    );
}

function TackledImpactDesktop({ list }: { list: Solution[] }) {
    const [index, setIndex] = useState(0);
    const [dir, setDir] = useState(1);
    const [expanded, setExpanded] = useState(false);
    const { ref, inView } = useInView<HTMLDivElement>();
    const sol = list[index % list.length];
    const stories = storiesOf(sol);

    const go = (step: number) => {
        setDir(step);
        setIndex((i) => (i + step + list.length) % list.length);
    };
    const goTo = (target: number) => {
        if (target === index) return;
        setDir(target > index ? 1 : -1);
        setIndex(target);
    };

    return (
        <FigmaFrame designWidth={1920} designHeight={1101} className="w-full">
            <div
                ref={ref}
                data-expanded={expanded}
                className={`relative h-[1101px] w-[1920px] overflow-hidden bg-[#f5f5f5] font-['Plus_Jakarta_Sans'] transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    inView ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
            >
                {/* Eyebrow (shared) */}
                <p className="absolute left-[76px] top-[36px] text-[32px] font-extrabold leading-[92px] text-[#1b1b1b]">
                    Our Solutions
                </p>

                {/* Shared statue — glides from the centred frame to the narrow left strip.
                    Uses transform + clip-path (both GPU-composited) for a slick, jank-free
                    ride: it slides left while its right edge is progressively cropped. */}
                <div
                    className="absolute left-[812px] top-[150px] h-[920px] w-[613px] overflow-hidden transition-[transform,clip-path] duration-[850ms] ease-[cubic-bezier(0.83,0,0.17,1)] will-change-transform"
                    style={{
                        transform: expanded ? 'translateX(-812px)' : 'translateX(0)',
                        clipPath: expanded ? 'inset(0 364px 0 0)' : 'inset(0 0 0 0)',
                    }}
                >
                    <Img
                        src={sol.image || STATUE}
                        alt={sol.image ? sol.imageAlt : ''}
                        className="pointer-events-none absolute inset-y-0 left-0 h-full w-[613px] max-w-none object-cover object-center"
                    />
                </div>

                {/* ── Collapsed layer ── */}
                <div
                    className={`absolute inset-0 transition-[opacity,transform] duration-[550ms] ease-[cubic-bezier(0.65,0,0.35,1)] ${
                        expanded ? 'pointer-events-none -translate-x-8 opacity-0' : 'translate-x-0 opacity-100'
                    }`}
                >
                    {/* Per-solution content re-enters on slide change */}
                    <div
                        key={index}
                        className="cg-sol-enter"
                        style={{ '--cg-sol-dir': `${dir * 52}px` } as React.CSSProperties}
                    >
                        <p className="absolute left-[40px] top-[350px] text-[150px] font-bold leading-[144px] text-[#1b1b1b]">
                            {sol.num}
                        </p>
                        <TwoTone
                            top={sol.headTop}
                            bottom={sol.headBottom}
                            className="absolute left-[357px] top-[360px] w-[740px] whitespace-nowrap text-[72px] font-extrabold leading-[92px] text-[#1b1b1b]"
                        />
                        <p className="absolute left-[357px] top-[590px] w-[418px] text-[18px] font-normal leading-[28px] text-[#333]">
                            {sol.paragraph}
                        </p>
                    </div>

                    {/* Vertical divider */}
                    <div className="absolute left-[111px] top-[695px] h-[406px] w-px bg-black/20" />

                    {/* Vertical slide indicator (left of the paragraph) */}
                    <div className="absolute left-[276px] top-[598px] flex flex-col items-center gap-[14px] rounded-full bg-[#ececec] px-[7px] py-[13px]">
                        {list.map((s, i) => (
                            <button
                                key={s.num}
                                type="button"
                                onClick={() => goTo(i)}
                                aria-label={`Go to solution ${s.num}`}
                                aria-current={i === index}
                                className={`size-[9px] rounded-full transition-all duration-300 ${
                                    i === index ? 'bg-[#1b1b1b]' : 'bg-[#c4c4c4] hover:bg-[#a0a0a0]'
                                }`}
                            />
                        ))}
                    </div>

                    {/* Previous / Next */}
                    <div className="absolute left-[357px] top-[792px] flex items-center gap-[24px]">
                        <button
                            type="button"
                            onClick={() => go(-1)}
                            className="flex h-[64px] w-[175px] items-center justify-center rounded-[12px] bg-[#1b1b1b] text-[18px] font-semibold text-white transition-colors hover:bg-black"
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            onClick={() => go(1)}
                            className="flex h-[64px] w-[175px] items-center justify-center rounded-[12px] bg-[#1b1b1b] text-[18px] font-semibold text-white transition-colors hover:bg-black"
                        >
                            Next
                        </button>
                    </div>

                    {/* Impact at a glance */}
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="group absolute left-[1527px] top-[500px] flex flex-col items-start"
                    >
                        <span className="flex size-[124px] items-center justify-center rounded-full bg-[#1b1b1b] text-white transition-transform duration-300 group-hover:scale-[1.06]">
                            <ArrowUpRight className="size-[44px]" />
                        </span>
                        <span className="mt-[20px] w-[117px] text-left text-[24px] font-bold leading-[32px] text-[#1b1b1b]">
                            Impact at a glance
                        </span>
                    </button>
                </div>

                {/* ── Expanded layer ── */}
                <div
                    className={`absolute inset-0 transition-opacity duration-[650ms] ease-out ${
                        expanded ? 'opacity-100' : 'pointer-events-none opacity-0'
                    }`}
                >
                    {/* Back — sits just above the heading */}
                    <button
                        type="button"
                        onClick={() => setExpanded(false)}
                        className="group absolute left-[470px] top-[140px] flex items-center gap-[12px]"
                    >
                        <span className="flex size-[45px] items-center justify-center rounded-full bg-[#1b1b1b] text-white transition-transform duration-300 group-hover:-translate-x-1">
                            <ArrowLeft className="size-[20px]" />
                        </span>
                        <span className="text-[24px] font-bold leading-[32px] text-[#1b1b1b]">Back</span>
                    </button>

                    <div
                        key={index}
                        className="cg-sol-enter"
                        style={{ '--cg-sol-dir': `${dir * 52}px` } as React.CSSProperties}
                    >
                        <TwoTone
                            top={sol.headTop}
                            bottom={sol.headBottom}
                            className="absolute left-[470px] top-[210px] w-[740px] whitespace-nowrap text-[72px] font-extrabold leading-[92px] text-[#1b1b1b]"
                        />
                        <p className="absolute left-[470px] top-[420px] w-[586px] text-[18px] font-normal leading-[28px] text-[#333]">
                            {sol.paragraph}
                        </p>
                    </div>

                    {/* Detail blocks (stagger in on expand) */}
                    <div className="absolute left-[1241px] top-[195px] flex w-[526px] flex-col gap-[40px]">
                        {sol.details.map((block, i) => (
                            <div
                                key={block.title}
                                className={`flex flex-col gap-[6px] transition-[opacity,transform] duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                                    expanded ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                                }`}
                                style={{ transitionDelay: expanded ? `${0.28 + i * 0.12}s` : '0s' }}
                            >
                                <p className="text-[24px] font-bold leading-[32px] text-black">{block.title}</p>
                                <p className="text-[16px] font-normal leading-[24px] text-[#333]">{block.body}</p>
                            </div>
                        ))}
                    </div>

                    {/* Top Success Stories */}
                    <div
                        className={`absolute left-[470px] top-[615px] flex w-[1325px] flex-col gap-[20px] transition-[opacity,transform] duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                            expanded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                        }`}
                        style={{ transitionDelay: expanded ? '0.6s' : '0s' }}
                    >
                        {stories.length > 0 && (
                            <>
                                <div className="flex w-full items-center justify-between">
                                    <p className="text-[24px] font-bold leading-[32px] text-black">
                                        Top Success Stories
                                    </p>
                                    <Link
                                        to="/resources/stories"
                                        className="text-[16px] font-normal leading-[24px] text-[#f60] underline"
                                    >
                                        View All Stories
                                    </Link>
                                </div>
                                <div className="scrollbar-hide flex items-center gap-[24px] overflow-x-auto">
                                    {stories.map((story) => (
                                        <Link
                                            key={story.slug}
                                            to={`/resources/stories/${story.slug}`}
                                            className="flex shrink-0 items-center gap-[20px] rounded-[30px] bg-white p-[16px]"
                                        >
                                            <div className="h-[109px] w-[119px] shrink-0 overflow-hidden rounded-[15px] bg-[#d9d9d9]">
                                                <Img src={story.image} alt="" className="size-full object-cover" />
                                            </div>
                                            <p className="line-clamp-4 w-[232px] shrink-0 text-[16px] font-bold leading-[24px] text-[#333]">
                                                {story.title}
                                            </p>
                                        </Link>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </FigmaFrame>
    );
}

/* ── Mobile ──────────────────────────────────────────────────────────── */

/** Bottom-sheet modal carrying a solution's "Impact at a glance" detail. */
function ImpactSheet({ sol, onClose }: { sol: Solution; onClose: () => void }) {
    const stories = storiesOf(sol);
    // Close on Escape, and freeze the page behind the sheet while it's open.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        const scroller = document.querySelector<HTMLElement>('.page-scroll-root');
        const prev = scroller?.style.overflow;
        if (scroller) scroller.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            if (scroller) scroller.style.overflow = prev ?? '';
        };
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-[3000] font-['Plus_Jakarta_Sans']">
            {/* Scrim — tap to dismiss */}
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="cg-sheet-backdrop absolute inset-0 w-full cursor-default bg-black/55"
            />

            {/* Sheet */}
            <div
                role="dialog"
                aria-modal="true"
                aria-label={`Impact at a glance — ${sol.headTop}${sol.headBottom}`}
                className="cg-sheet absolute inset-x-0 bottom-0 flex max-h-[86dvh] flex-col rounded-t-[24px] bg-white shadow-[0_-12px_40px_rgba(0,0,0,0.25)]"
            >
                {/* Grab handle */}
                <div className="flex shrink-0 justify-center pt-[10px]">
                    <span className="h-[4px] w-[44px] rounded-full bg-[#dcdcdc]" />
                </div>

                {/* Header */}
                <div className="flex shrink-0 items-start justify-between gap-4 px-5 pb-4 pt-3">
                    <div>
                        <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#8a8a8a]">
                            Impact at a glance
                        </p>
                        <h3 className="mt-1 text-[22px] font-extrabold leading-[30px] text-[#1b1b1b]">
                            <span className="text-[#6a6854]">{sol.headTop}</span>
                            <span>{sol.headBottom}</span>
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0] text-[#1b1b1b]"
                    >
                        <CloseIcon className="size-4" />
                    </button>
                </div>

                <div className="h-px shrink-0 bg-[#ececec]" />

                {/* Scrollable body */}
                <div className="scrollbar-hide flex-1 overflow-y-auto px-5 pb-10 pt-6">
                    <div className="flex flex-col gap-7">
                        {sol.details.map((block) => (
                            <div key={block.title} className="flex flex-col gap-1.5">
                                <p className="text-[20px] font-bold leading-[28px] text-black">{block.title}</p>
                                <p className="text-[15px] font-normal leading-[22px] text-[#333]">{block.body}</p>
                            </div>
                        ))}
                    </div>

                    {stories.length > 0 && (
                        <div className="mt-10">
                            <div className="mb-4 flex items-center justify-between">
                                <p className="text-[20px] font-bold leading-[28px] text-black">Top Success Stories</p>
                                <Link to="/resources/stories" className="text-[14px] text-[#f60] underline">
                                    View All Stories
                                </Link>
                            </div>
                            <div className="scrollbar-hide -mx-5 flex gap-4 overflow-x-auto px-5 pb-2">
                                {stories.map((story) => (
                                    <Link
                                        key={story.slug}
                                        to={`/resources/stories/${story.slug}`}
                                        className="flex w-[300px] shrink-0 items-center gap-4 rounded-[24px] bg-[#f5f5f5] p-3"
                                    >
                                        <div className="h-[88px] w-[96px] shrink-0 overflow-hidden rounded-[12px] bg-[#d9d9d9]">
                                            <Img src={story.image} alt="" className="size-full object-cover" />
                                        </div>
                                        <p className="line-clamp-3 flex-1 text-[14px] font-bold leading-[20px] text-[#333]">
                                            {story.title}
                                        </p>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function TackledImpactMobile({ list }: { list: Solution[] }) {
    const [index, setIndex] = useState(0);
    const [dir, setDir] = useState(1);
    const [sheetOpen, setSheetOpen] = useState(false);
    const sol = list[index % list.length];
    const go = (step: number) => {
        setDir(step);
        setIndex((i) => (i + step + list.length) % list.length);
    };
    const goTo = (target: number) => {
        if (target === index) return;
        setDir(target > index ? 1 : -1);
        setIndex(target);
    };

    return (
        <section className="overflow-hidden bg-[#f5f5f5] px-5 pb-14 pt-12 font-['Plus_Jakarta_Sans']">
            <p className="text-[24px] font-extrabold leading-[36px] text-[#1b1b1b]">Our Solutions</p>

            <div
                key={index}
                className="cg-sol-enter"
                style={{ '--cg-sol-dir': `${dir * 40}px` } as React.CSSProperties}
            >
                <p className="mt-7 text-[76px] font-bold leading-[1] text-[#1b1b1b]">{sol.num}</p>

                <h2 className="mt-7 max-w-[335px] text-[40px] font-extrabold leading-[50px] text-[#1b1b1b]">
                    <span className="text-[#6a6854]">{sol.headTop}</span>
                    <span>{sol.headBottom}</span>
                </h2>

                <p className="mt-7 max-w-[335px] text-[14px] font-normal leading-[22px] text-[#333]">{sol.paragraph}</p>

                <div className="mt-8 overflow-hidden rounded-[16px]">
                    <Img
                        src={sol.image || STATUE}
                        alt={sol.image ? sol.imageAlt : ''}
                        className="h-[360px] w-full object-cover object-center"
                    />
                </div>

                <button type="button" onClick={() => setSheetOpen(true)} className="mt-7 flex items-center gap-4">
                    <span className="flex size-[64px] items-center justify-center rounded-full bg-[#1b1b1b] text-white">
                        <ArrowUpRight className="size-6" />
                    </span>
                    <span className="text-[20px] font-bold leading-[28px] text-[#1b1b1b]">Impact at a glance</span>
                </button>
            </div>

            {sheetOpen && <ImpactSheet sol={sol} onClose={() => setSheetOpen(false)} />}

            {/* Slide indicator + Previous / Next */}
            <div className="mt-9 flex flex-col gap-5">
                <div
                    className="flex items-center gap-[10px] rounded-full bg-[#ececec] px-[10px] py-[7px]"
                    style={{ width: 'fit-content' }}
                >
                    {list.map((s, i) => (
                        <button
                            key={s.num}
                            type="button"
                            onClick={() => goTo(i)}
                            aria-label={`Go to solution ${s.num}`}
                            aria-current={i === index}
                            className={`size-[8px] rounded-full transition-all duration-300 ${
                                i === index ? 'bg-[#1b1b1b]' : 'bg-[#c4c4c4]'
                            }`}
                        />
                    ))}
                </div>
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={() => go(-1)}
                        className="flex h-[52px] flex-1 items-center justify-center rounded-[12px] bg-[#1b1b1b] text-[16px] font-semibold text-white"
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        onClick={() => go(1)}
                        className="flex h-[52px] flex-1 items-center justify-center rounded-[12px] bg-[#1b1b1b] text-[16px] font-semibold text-white"
                    >
                        Next
                    </button>
                </div>
            </div>
        </section>
    );
}

export default function TackledImpact() {
    const list = solutions();
    if (!list.length) return null;
    return (
        <>
            <div className="hidden lg:block">
                <TackledImpactDesktop list={list} />
            </div>
            <div className="lg:hidden">
                <TackledImpactMobile list={list} />
            </div>
        </>
    );
}
